import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-styled-flexboxgrid';
import { toast } from 'react-toastify';
import { ApiAuth } from '../../../../api/auth';
import { ApiRoles } from '../../../../api/roles';
import { ApiSuppliers } from '../../../../api/suppliers';
import AppBar from '../../../../components/AppBar';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import Layout from '../../../../components/Layout';
import Loader from '../../../../components/Loader';
import Modal from '../../../../components/Modal';
import Search from '../../../../components/Search';
import TabsLinks from '../../../../components/TabsLinks';
import Typography from '../../../../components/Typography';
import FormConfirmDelete from '../../../../forms/FormConfirmDelete';
import FormUser from '../../../../forms/FormUser';
import { useApi } from '../../../../hooks/useApi';
import { useManage } from '../../../../hooks/useManage';
import useTheme from '../../../../hooks/useTheme';
import { paths } from '../../../../routes';
import { OPERATIONS } from '../../../../utils/constans';
import { filterDataByString, findByID, parseUsers } from '../../../../utils/helpers';
import TableUsers from './components/TableUsers';
import { StyledUsers, StyledUsersContent } from './styled';

const Users = () => {
    const { t } = useTranslation()
    const theme = useTheme();
    const [{ operation, id: activeID }, setState] = useManage();

    const [searchShow, setSearchShow] = useState(false);
    const [searchText, setSearchText] = useState("");

    // Setup request
    const [{ loading: registerManagerLoading }, registerManager] = useApi(ApiAuth.registerManager, { manual: true });
    const [{ data: managerRoles, loading: managerRolesLoading }] = useApi(ApiRoles.getManagerRoles);
    const [{ data: users, loading: usersLoading }, getUsers] = useApi(ApiSuppliers.get);
    const [{ loading: updateSupplierLoading }, updateSupplier] = useApi(ApiSuppliers.update, { manual: true });
    const [{ loading: deleteSupplierLoading }, deleteSupplier] = useApi(ApiSuppliers.delete, { manual: true });

    // Memoize data
    const usersParsed = useMemo(
        () => parseUsers(users),
        [users]
    );

    const usersFiltered = useMemo(
        () => {
            return filterDataByString(usersParsed, searchText, user => user?.username);
        },
        [usersParsed, searchText]
    );

    const active = useMemo(
        () => usersFiltered && findByID(usersFiltered, activeID),
        [usersFiltered, activeID]
    );

    // Click handlers
    const handleClickAdd = () => setState(OPERATIONS.ADD);
    const handleClickEdit = (id) => setState(OPERATIONS.EDIT, id);
    const handleClickDelete = (id) => setState(OPERATIONS.DELETE, id);
    const handleClickCancelModal = () => setState(OPERATIONS.READ, null);

    const handleChangeSearch = e => {
        setSearchText(e.target.value);
    };

    const handleClickSearchIcon = () => {
        setSearchShow(prevState => !prevState);
    };

    // Submit handlers
    const handleSubmit = async data => {
        if (operation === OPERATIONS.EDIT) {
            //UPDATE
            const res = await updateSupplier(activeID, data);
            if (res?.data) {
                toast.success(t('Użytkownik został zaktualizowany'));
                setState(OPERATIONS.READ, null);
                getUsers();
            }
        } else {
            //CREATE
            const res = await registerManager(data);
            if (res?.data) {
                toast.success(t('Użytkownik został dodany'));
                setState(OPERATIONS.READ, null);
                getUsers();
            }
        }
    };

    const handleSubmitDelete = async () => {
        //DELETE
        const res = await deleteSupplier(activeID);
        if (res?.data) {
            toast.success(t('Użytkownik został usunięty'));
            setState(OPERATIONS.READ, null);
            getUsers();
        }
    }

    return (
        <StyledUsers>
            <AppBar>
                <Typography
                    font={theme.fonts.mulishRegular}
                    as="h2"
                >
                    {t("Panel administracyjny")}
                </Typography>
            </AppBar>
            <TabsLinks
                tabs={[{
                    to: paths.users,
                    name: t('Użytkownicy')
                }]}
                rightContent={
                    <Row middle="xs">
                        <Col>
                            <Search
                                value={searchText}
                                onChange={handleChangeSearch}
                                expanded={searchShow}
                                onClickSearchIcon={handleClickSearchIcon}
                            />
                        </Col>
                        <Col>
                            <Button
                                startIcon={
                                    <Icon
                                        name="add"
                                        width={12}
                                        height={12}
                                    />
                                }
                                onClick={handleClickAdd}
                            >
                                {t("Dodaj użytkownika")}
                            </Button>
                        </Col>
                    </Row>
                }
            />
            <Layout>
                <StyledUsersContent>
                    <TableUsers
                        users={usersFiltered}
                        onClickEdit={handleClickEdit}
                        onClickDelete={handleClickDelete}
                    />
                </StyledUsersContent>
            </Layout>
            <Modal
                open={operation === OPERATIONS.ADD || operation === OPERATIONS.EDIT}
            >
                <FormUser
                    loading={managerRolesLoading || registerManagerLoading || updateSupplierLoading}
                    edit={operation === OPERATIONS.EDIT}
                    initialValues={operation === OPERATIONS.EDIT ? active?.form_values : {}}
                    roles={managerRoles}
                    onSubmit={handleSubmit}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal
                open={operation === OPERATIONS.DELETE}
            >
                <FormConfirmDelete
                    loading={deleteSupplierLoading}
                    onSubmit={handleSubmitDelete}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Loader show={registerManagerLoading || usersLoading || updateSupplierLoading || deleteSupplierLoading || managerRolesLoading} />
        </StyledUsers>
    );
};

export default Users;
